// Utilities to export/import the saved session in localStorage to/from CSV/Excel
import * as XLSX from 'xlsx';
const SCHEDULES_STORAGE_KEY = 'fourlink-config-tool-schedules-v1';

const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

// Flatten a JSON object into rows of [path, valueJson]
function flattenToRows(obj) {
  const rows = [];
  const isObject = (v) => v && typeof v === 'object' && !Array.isArray(v);

  const walk = (value, pathParts) => {
    const isLeaf =
      value === null ||
      typeof value !== 'object' ||
      (Array.isArray(value) && value.length === 0) ||
      (isObject(value) && Object.keys(value).length === 0);
    if (isLeaf) {
      rows.push([pathParts.join('.'), JSON.stringify(value)]);
      return;
    }
    if (Array.isArray(value)) {
      for (let i = 0; i < value.length; i++) {
        walk(value[i], pathParts.concat(String(i)));
      }
      return;
    }
    if (isObject(value)) {
      for (const k of Object.keys(value)) {
        walk(value[k], pathParts.concat(k));
      }
    }
  };

  walk(obj, []);
  return rows;
}

// Build object from rows of [path, valueJson]
function unflattenFromRows(rows) {
  const root = {};

  const isIndex = (s) => /^\d+$/.test(s);

  for (const [path, valueJson] of rows) {
    const parts = path ? path.split('.') : [];
    let cursor = root;
    const value = JSON.parse(valueJson);

    for (let i = 0; i < parts.length; i++) {
      const seg = parts[i];
      const last = i === parts.length - 1;
      const nextSeg = parts[i + 1];
      const nextIsIndex = isIndex(nextSeg || '');

      if (last) {
        if (isIndex(seg)) {
          // cursor must be an array already
          const idx = Number(seg);
          if (!Array.isArray(cursor)) {
            // Convert object to array if needed
            const arr = [];
            Object.assign(arr, cursor);
            // This assignment won't change external reference; ensure caller set array before
          }
          cursor[idx] = value;
        } else {
          cursor[seg] = value;
        }
        continue;
      }

      if (isIndex(seg)) {
        const idx = Number(seg);
        if (!Array.isArray(cursor)) {
          // Should not happen if previous step created the array
          // But create a temp array in place if needed
          // This case only occurs if root is an array at top level
        }
        if (cursor[idx] === undefined) {
          cursor[idx] = nextIsIndex ? [] : {};
        }
        cursor = cursor[idx];
      } else {
        if (cursor[seg] === undefined) {
          cursor[seg] = nextIsIndex ? [] : {};
        }
        cursor = cursor[seg];
      }
    }
    // Edge case: empty path -> whole object
    if (parts.length === 0) {
      return value;
    }
  }
  return root;
}

function csvEscape(value) {
  // Wrap in quotes and escape quotes by doubling
  const text = String(value);
  const escaped = text.replace(/"/g, '""');
  return `"${escaped}"`;
}

function buildCsv(rows) {
  const header = 'path,value';
  const lines = [header];
  for (const [path, valueJson] of rows) {
    lines.push(`${csvEscape(path)},${csvEscape(btoa(unescape(encodeURIComponent(valueJson))))}`);
  }
  return lines.join('\n');
}

function parseCsv(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (lines.length === 0) return [];
  const startIdx = lines[0].toLowerCase().startsWith('path') ? 1 : 0;
  const rows = [];

  // Simple CSV two-column parser expecting quoted fields
  const parseLine = (line) => {
    const fields = [];
    let i = 0;
    for (let f = 0; f < 2; f++) {
      if (line[i] !== '"') {
        // unquoted till comma
        const j = line.indexOf(',', i);
        const val = j === -1 ? line.slice(i) : line.slice(i, j);
        fields.push(val);
        i = j === -1 ? line.length : j + 1;
      } else {
        // quoted
        i++; // skip opening quote
        let buf = '';
        while (i < line.length) {
          const ch = line[i];
          if (ch === '"') {
            if (i + 1 < line.length && line[i + 1] === '"') {
              buf += '"';
              i += 2;
            } else {
              i++;
              break;
            }
          } else {
            buf += ch;
            i++;
          }
        }
        fields.push(buf);
        if (i < line.length && line[i] === ',') i++;
      }
    }
    return fields;
  };

  for (let idx = startIdx; idx < lines.length; idx++) {
    const line = lines[idx];
    const [path, valueB64] = parseLine(line);
    try {
      const valueJson = decodeURIComponent(escape(atob(valueB64)));
      rows.push([path, valueJson]);
    } catch (e) {
      // skip malformed line
    }
  }
  return rows;
}

export function exportLocalStorageToCsv(storageKey, filename = '4link-config.csv') {
  if (typeof window === 'undefined' || !window.localStorage) return;
  const raw = window.localStorage.getItem(storageKey);
  if (!raw) {
    alert('No hay sesion guardada para exportar.');
    return;
  }
  let obj;
  try {
    obj = JSON.parse(raw);
  } catch (e) {
    alert('El estado guardado no es JSON valido.');
    return;
  }
  let schedules = [];
  try {
    const schedulesRaw = window.localStorage.getItem(SCHEDULES_STORAGE_KEY);
    if (schedulesRaw) {
      const parsedSchedules = JSON.parse(schedulesRaw);
      if (Array.isArray(parsedSchedules)) {
        schedules = parsedSchedules;
      }
    } else {
      schedules = [];
    }
  } catch (_) {
    schedules = [];
  }

  const payload = {
    session: obj,
    schedules,
  };

  const rows = flattenToRows(payload);
  const csv = buildCsv(rows);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export async function importCsvFileToLocalStorage(file, storageKey) {
  const text = await file.text();
  const rows = parseCsv(text);
  const payload = unflattenFromRows(rows);
  const hasWrapper = isPlainObject(payload) && isPlainObject(payload.session);
  const sessionData = hasWrapper ? payload.session : payload;

  if (!isPlainObject(sessionData)) {
    throw new Error('El CSV no contiene una sesión válida.');
  }

  if (typeof window !== 'undefined' && window.localStorage) {
    window.localStorage.setItem(storageKey, JSON.stringify(sessionData));

    let schedulesProvided = false;
    let schedulesData = [];

    if (hasWrapper && Object.prototype.hasOwnProperty.call(payload, 'schedules')) {
      schedulesProvided = true;
      schedulesData = Array.isArray(payload.schedules) ? payload.schedules : [];
    } else if (Object.prototype.hasOwnProperty.call(sessionData, 'horariosDisponibles')) {
      schedulesProvided = true;
      schedulesData = Array.isArray(sessionData.horariosDisponibles)
        ? sessionData.horariosDisponibles
        : [];
    }

    if (schedulesProvided) {
      if (Array.isArray(schedulesData) && schedulesData.length > 0) {
        window.localStorage.setItem(SCHEDULES_STORAGE_KEY, JSON.stringify(schedulesData));
      } else {
        window.localStorage.removeItem(SCHEDULES_STORAGE_KEY);
      }
    }
  }
  return sessionData;
}

const extractSnapshotFromWorkbook = (workbook) => {
  const snapshotSheetName = workbook.SheetNames.find((name) => name.toLowerCase() === 'snapshot') || workbook.SheetNames[0];
  const sheet = workbook.Sheets[snapshotSheetName];
  if (!sheet) {
    throw new Error('El Excel no contiene hoja de snapshot.');
  }
  // Expect a table with key/value columns; look for snapshot_json
  const range = XLSX.utils.sheet_to_json(sheet, { header: 1 });
  let snapshotJson = null;
  for (const row of range) {
    if (!Array.isArray(row) || row.length < 2) continue;
    const key = String(row[0] || '').trim().toLowerCase();
    if (key === 'snapshot_json') {
      snapshotJson = row[1];
      break;
    }
  }
  if (!snapshotJson && sheet.B2) {
    snapshotJson = sheet.B2.v;
  }
  if (!snapshotJson || typeof snapshotJson !== 'string') {
    throw new Error('No se encontró snapshot_json en el Excel.');
  }
  return JSON.parse(snapshotJson);
};

export async function importSessionFile(file, storageKey) {
  const name = (file?.name || '').toLowerCase();
  const isExcel = name.endsWith('.xlsx') || name.endsWith('.xls');

  if (isExcel) {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'array' });
    const snapshot = extractSnapshotFromWorkbook(workbook);
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(storageKey, JSON.stringify(snapshot));
      if (Array.isArray(snapshot.horariosDisponibles) && snapshot.horariosDisponibles.length > 0) {
        window.localStorage.setItem(SCHEDULES_STORAGE_KEY, JSON.stringify(snapshot.horariosDisponibles));
      }
    }
    return snapshot;
  }

  return importCsvFileToLocalStorage(file, storageKey);
}

// Optional: helpers for direct object export/import
export function serializeObjectToCsv(obj, filename = '4link-config.csv') {
  const rows = flattenToRows(obj);
  const csv = buildCsv(rows);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

